<script setup lang="ts">
import AddSparePartsForm, {
  type ISparePartsForm,
  type IValidPartForm,
} from '@/components/rc/spare-parts/AddSparePartsForm.vue'
import AddSparePartsTable from '@/components/rc/spare-parts/AddSparePartsTable.vue'
import { useSpareParts } from '@/composables/rc/spare-parts'
import { NotifySuccess } from '@/helpers/core/notifications'
import type { ISparePartCreationForm } from '@/types/rc/forms'
import { PartType } from '@/types/rc/miners/PartType'
import { type ISparePart } from '@/types/rc/spare-parts'
import { useQueryClient } from '@tanstack/vue-query'
import { ElMessage } from 'element-plus'
import { ref } from 'vue'

const open = defineModel<boolean>()

// Parts that have been actually added by the BE
const addedParts = ref<ISparePart[]>([])

// Provide an empty array for SNs
const initialFormState = () => structuredClone({ part_type: PartType.PowerSupply, part_sns: [] })

// Part on transit through the form
const newPart = ref<ISparePartsForm>(initialFormState())

// Form reference to reset and validate
const partForm = ref()

const onCloseDialog = () => {
  newPart.value = initialFormState()
  partForm.value?.reset()
  addedParts.value = []
}

// BE calls

const client = useQueryClient()
const { addSparesMutation } = useSpareParts()

const addParts = async () => {
  try {
    await partForm.value?.validate()
    const validPart = newPart.value as IValidPartForm
    await addManySpareParts(
      {
        site_id: validPart.site.site_id,
        customer_id: validPart.customer.id,
        part_type: validPart.part_type,
        part_model: { name: validPart.part_model, version: null },
        price: validPart.price,
      },
      validPart.part_sns // These are optional for fans
    )
  } catch {
    ElMessage.error('Please fill in all the required fields')
  }
}

/**
 * Adds many spare parts with the same form but different SNs.
 * @param basePart the spare part to be cloned with different SNs
 * @param sns the SNs to clone the base part with
 */
const addManySpareParts = async (basePart: ISparePartCreationForm, sns: (string | undefined)[]) => {
  try {
    const transactionAddedParts = await Promise.all(
      sns.map((sn) => addSparesMutation.mutateAsync({ ...basePart, part_sn: sn }))
    )
    addedParts.value.push(...transactionAddedParts)
    NotifySuccess(`${transactionAddedParts.length} parts added successfully`)
    partForm.value?.reset()
    client.invalidateQueries({ queryKey: ['spare-parts'] })
  } catch (e: any) {
    ElMessage.error(e.message)
  }
}
</script>

<template>
  <el-dialog
    v-model="open"
    class="target"
    title="Add Spare Parts"
    :loading="addSparesMutation.isPending"
    :close-on-press-escape="false"
    width="1236px"
    height="800px"
    @closed="onCloseDialog"
  >
    <el-container>
      <el-aside width="400px">
        <AddSparePartsForm ref="partForm" v-model="newPart" @add="addParts" />
      </el-aside>
      <el-main>
        <AddSparePartsTable v-model="addedParts" />
      </el-main>
    </el-container>
  </el-dialog>
</template>

<style scoped lang="scss">
.el-main {
  padding: 0 var(--spacing-spacing-lg);
}

.el-aside {
  padding: var(--spacing-spacing-lg) 0;
}

.el-notification {
  &__group {
    width: 100%;

    .deletionMessage {
      display: flex;
      gap: var(--spacing-spacing-xs);
      align-items: center;
      justify-content: space-between;

      .el-button {
        color: var(--text-brand);
      }
    }
  }
}
</style>
